import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { UserRole, type AuthUser } from '@teentack/shared';
import { useAuth } from '@/auth/use-auth';
import { useGuardianRequests } from '@/hooks/use-guardian-requests';
import { useConsentQueue } from '@/features/consent/use-consent';
import { useTeenOverview } from '@/features/teen/use-teen-overview';
import { usePosterOverview } from '@/features/poster/use-poster-overview';
import { useUnreadMessageCount } from '@/features/messaging/use-messaging';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { Logo } from '@/components/Logo';
import { buttonClasses } from '@/components/ui/button-styles';
import { CloseIcon, MenuIcon, UserCheckIcon } from '@/components/ui/Icons';

/**
 * Primary navigation.
 *
 * Signed out, this is a marketing site and the nav sells the product. Signed
 * in, it is an app: it shows the routes that role can actually use. A parent
 * has no business seeing "Post a Job" — that link goes to the signup wizard,
 * which is nonsense for someone who already has an account. [FR-M01.7]
 */

interface NavEntry {
  label: string;
  to: string;
  /** Rendered as a red count when non-zero — work waiting on this person. */
  badge?: number;
}

const MARKETING_LINKS: NavEntry[] = [
  { label: 'How It Works', to: '/#how-it-works' },
  { label: 'Find Job', to: '/jobs' },
  { label: 'Post Job', to: '/register' },
  // Young entrepreneurs list services rather than apply, but that surface does
  // not exist yet — until it does, "Shop" lands on the same browse screen.
  { label: 'Shop', to: '/jobs' },
];

/** "Ava Nguyen" -> "AN". Falls back to the first character for one-word names. */
function initialsOf(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + last).toUpperCase();
}

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-lg px-3 py-2 text-sm font-medium transition duration-200 ${
    isActive ? 'text-brand-600' : 'text-ink-soft hover:text-ink'
  }`;

function Badge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[0.65rem] font-bold text-white">
      {count}
    </span>
  );
}

export function SiteHeader() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [lastNavKey, setLastNavKey] = useState(location.key);

  const isParent = user?.role === UserRole.PARENT;
  // Two different things wait on a parent, and each gets its own count so the
  // badge always sits on the link that actually has something to do.
  const teenAccounts = useGuardianRequests(isParent);
  const teenOffers = useTeenOverview(user?.role === UserRole.TEEN);
  const posterQueue = usePosterOverview(user?.role === UserRole.JOB_POSTER);
  const jobApprovals = useConsentQueue({ status: 'PENDING', pageSize: 1 }, isParent);
  // Every signed-in role messages, so this one is not role-gated.
  const unreadMessages = useUnreadMessageCount(Boolean(user));

  // Any navigation closes the mobile sheet — adjusted during render rather than
  // in an effect, so it never causes a second paint with the sheet still open.
  if (location.key !== lastNavKey) {
    setLastNavKey(location.key);
    setMenuOpen(false);
  }

  /** The routes this account can actually use. */
  function appLinks(current: AuthUser): NavEntry[] {
    switch (current.role) {
      case UserRole.TEEN:
        // The console owns its own sub-navigation, so the header stays short.
        return [
          { label: 'My TeenTack', to: '/teen' },
          { label: 'Find Jobs', to: '/jobs' },
          { label: 'My work', to: '/my-work', badge: teenOffers.data?.jobOffers ?? 0 },
          { label: 'Messages', to: '/messages', badge: unreadMessages.data?.unread ?? 0 },
        ];
      case UserRole.PARENT:
        // The console owns its own sub-navigation, so the header stays short.
        return [
          { label: 'Console', to: '/parent' },
          {
            label: 'Approvals',
            to: '/approvals',
            badge: (jobApprovals.data?.total ?? 0) + (teenAccounts.data?.length ?? 0),
          },
          { label: 'Browse jobs', to: '/jobs' },
          { label: 'Messages', to: '/messages', badge: unreadMessages.data?.unread ?? 0 },
        ];
      case UserRole.JOB_POSTER:
        // The console owns its own sub-navigation, so the header stays short.
        return [
          { label: 'Console', to: '/poster' },
          { label: 'My jobs', to: '/my-jobs' },
          {
            label: 'Applicants',
            to: '/applicants',
            badge: posterQueue.data?.newApplications ?? 0,
          },
          { label: 'Messages', to: '/messages', badge: unreadMessages.data?.unread ?? 0 },
        ];
      default:
        return [];
    }
  }

  const links: NavEntry[] = user ? appLinks(user) : MARKETING_LINKS;
  // Signed out this is a marketing site, and the design floats the nav as a
  // rounded card over the hero. Signed in it is an app chrome, so it stays a
  // plain bar pinned to the top edge.
  const floating = !user;

  return (
    <header
      className={`sticky top-0 z-40 ${
        floating ? 'pt-3 sm:pt-4' : 'border-b border-line bg-white/90 backdrop-blur'
      }`}
    >
      <div className={floating ? 'container-page' : ''}>
        <div
          className={`flex h-16 items-center justify-between gap-4 sm:h-18 ${
            floating
              ? 'rounded-2xl border border-line bg-white/95 px-4 shadow-soft backdrop-blur sm:px-6'
              : 'container-page'
          }`}
        >
          <Logo />

          <nav aria-label="Main" className="hidden items-center gap-0.5 lg:flex">
            {links.map((link) =>
              link.to.includes('#') ? (
                <Link key={link.label} to={link.to} className={linkClass({ isActive: false })}>
                  {link.label}
                </Link>
              ) : (
                <NavLink key={link.label} to={link.to} className={linkClass} end>
                  {link.label}
                  <Badge count={link.badge ?? 0} />
                </NavLink>
              ),
            )}
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            {user ? (
              <>
                <NotificationBell />
                <Link
                  to="/profile"
                  className="flex items-center gap-2 rounded-xl px-2 py-1.5 transition duration-200 hover:bg-brand-50"
                >
                  <span
                    aria-hidden="true"
                    className="bg-brand-gradient grid size-8 shrink-0 place-items-center rounded-lg text-xs font-extrabold text-white"
                  >
                    {initialsOf(user.displayName)}
                  </span>
                  <span className="max-w-32 truncate text-sm font-semibold text-ink-soft">
                    {user.displayName}
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => void logout()}
                  className={buttonClasses('outline', 'sm')}
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={buttonClasses(
                    'outline',
                    'sm',
                    'rounded-lg border-transparent bg-accent-50 px-4 hover:bg-accent-100',
                  )}
                >
                  Login
                  <UserCheckIcon className="size-4" />
                </Link>
                <Link to="/register" className={buttonClasses('primary', 'sm', 'rounded-lg px-4')}>
                  Sign Up
                  <UserCheckIcon className="size-4" />
                </Link>
              </>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            className="rounded-xl border border-line p-2 text-ink transition duration-200 hover:bg-brand-50 lg:hidden"
          >
            {menuOpen ? <CloseIcon className="size-5" /> : <MenuIcon className="size-5" />}
          </button>
        </div>

        {menuOpen && (
          <div
            id="mobile-nav"
            className={`bg-white lg:hidden ${
              floating
                ? 'mt-2 rounded-2xl border border-line px-4 shadow-soft'
                : 'border-t border-line'
            }`}
          >
            <nav
              aria-label="Main (mobile)"
              className={`flex flex-col py-3 ${floating ? '' : 'container-page'}`}
            >
              {links.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className="flex items-center rounded-lg px-2 py-2.5 text-base font-semibold text-ink transition duration-200 hover:bg-brand-50 hover:text-brand-700"
                >
                  {link.label}
                  <Badge count={link.badge ?? 0} />
                </Link>
              ))}

              <div className="mt-3 flex gap-2 border-t border-line pt-3">
                {user ? (
                  <>
                    <Link to="/notifications" className={buttonClasses('outline', 'md', 'flex-1')}>
                      Alerts
                    </Link>
                    <Link to="/money" className={buttonClasses('outline', 'md', 'flex-1')}>
                      Money
                    </Link>
                    <Link to="/safety" className={buttonClasses('outline', 'md', 'flex-1')}>
                      Get help
                    </Link>
                    <Link to="/profile" className={buttonClasses('primary', 'md', 'flex-1')}>
                      My profile
                    </Link>
                    <button
                      type="button"
                      onClick={() => void logout()}
                      className={buttonClasses('outline', 'md', 'flex-1')}
                    >
                      Sign out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className={buttonClasses('outline', 'md', 'flex-1')}>
                      Log In
                    </Link>
                    <Link to="/register" className={buttonClasses('primary', 'md', 'flex-1')}>
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
